/**
 * Shared citation plumbing (App Review 1.4.1).
 *
 * Apple's rejection wording asks for citations "such as links to those
 * sources", and for them to be "easy for the user to find". Build 96 shipped a
 * citation renderer whose link path never fired because no source carried a
 * URL, so every reference rendered as grey text. Two rules follow from that:
 *
 *   1. A citation without a resolvable URL does not count. Author-year text is
 *      not a citation as far as review is concerned.
 *   2. Every surface that shows a medical claim uses THIS module, so we can
 *      never again have one screen cited and five screens not.
 *
 * A URL is only rendered as a citation when its host is on the allowlist
 * below. That keeps a stray blog or vendor link from ever being presented to a
 * user as a medical source, and it is the check the References screen uses to
 * assert full coverage.
 */

/** Publisher class, used for the small label on a citation and for grouping. */
export type SourceKind =
  | 'regulatory'
  | 'peer_reviewed'
  | 'government_health'
  | 'standards'
  | 'clinical_trial';

export interface Citation {
  /** Human-readable reference, e.g. "Wilding et al. 2021, NEJM". */
  name: string;
  /** Must be an allowlisted host, or the citation will not render as a link. */
  url: string;
  kind?: SourceKind;
}

/**
 * Exact hosts we accept as a medical citation. Suffix rules below cover the
 * government domains where subdomains are numerous (accessdata.fda.gov,
 * dailymed.nlm.nih.gov, and so on).
 */
const RECOGNIZED_HOSTS = [
  // Indexes and identifiers
  'pubmed.ncbi.nlm.nih.gov',
  'pmc.ncbi.nlm.nih.gov',
  'ncbi.nlm.nih.gov',
  'doi.org',
  'clinicaltrials.gov',
  'cochranelibrary.com',
  // Journals
  'nejm.org',
  'thelancet.com',
  'jamanetwork.com',
  'jci.org',
  'nature.com',
  'sciencedirect.com',
  'cell.com',
  'bmj.com',
  'physiology.org',
  'ahajournals.org',
  'diabetesjournals.org',
  'academic.oup.com',
  'link.springer.com',
  'onlinelibrary.wiley.com',
  'frontiersin.org',
  'mdpi.com',
  'karger.com',
  'tandfonline.com',
  'journals.plos.org',
  'annals.org',
  // Standards and public health bodies
  'usp.org',
  'go.drugbank.com',
  // World Anti-Doping Agency: the authority for whether a substance is banned
  // in sport, which several peptide pages state as fact.
  'wada-ama.org',
  // US regulatory primary sources. Needed to cite what the law actually says
  // about compounding status - the /learn guide previously asserted a change
  // in legal availability that these sources disprove.
  'federalregister.gov',
  'ecfr.gov',
  'dailymed.nlm.nih.gov',
] as const;

/** Domain suffixes accepted wholesale (US/-international public health bodies). */
const RECOGNIZED_SUFFIXES = ['.nih.gov', '.fda.gov', '.who.int', '.cdc.gov', '.gov.uk'] as const;

/** Bare apexes for the suffix list above. */
const RECOGNIZED_APEXES = ['fda.gov', 'who.int', 'cdc.gov', 'nih.gov'] as const;

/**
 * True when a URL points at a source we are willing to present as a medical
 * citation. Anything else renders as plain text (and is treated as a coverage
 * gap by the citation audit test).
 */
export function isRecognizedSourceUrl(url: string | undefined | null): boolean {
  if (!url) return false;
  let host: string;
  try {
    const parsed = new URL(url);
    // A non-https source is not acceptable as a citation: it can be rewritten
    // in transit, and iOS blocks the navigation under ATS anyway.
    if (parsed.protocol !== 'https:') return false;
    host = parsed.hostname.replace(/^www\./, '').toLowerCase();
  } catch {
    return false;
  }
  return (
    (RECOGNIZED_HOSTS as readonly string[]).includes(host) ||
    (RECOGNIZED_APEXES as readonly string[]).includes(host) ||
    RECOGNIZED_SUFFIXES.some((suffix) => host.endsWith(suffix))
  );
}

/** A citation counts only if it renders as a tappable link to a known source. */
export function isUsableCitation(c: { url?: string } | undefined | null): boolean {
  return isRecognizedSourceUrl(c?.url);
}

/** Drops anything that would render as un-tappable text. */
export function usableCitations<T extends { url?: string }>(sources: T[] | undefined | null): T[] {
  if (!sources) return [];
  return sources.filter((s) => isRecognizedSourceUrl(s.url));
}

const KIND_LABELS: Record<SourceKind, string> = {
  // Covers FDA prescribing information, eCFR and the Federal Register, so the
  // label has to be broader than "FDA label".
  regulatory: 'Regulatory',
  peer_reviewed: 'Peer-reviewed',
  government_health: 'Public health',
  standards: 'Standard',
  clinical_trial: 'Clinical trial',
};

export function sourceKindLabel(kind: SourceKind | undefined): string | null {
  return kind ? KIND_LABELS[kind] : null;
}

/**
 * Infers publisher class from the host so existing rows (which carry only a
 * name and url) still get a correct label without a data migration.
 */
export function inferSourceKind(url: string | undefined): SourceKind | undefined {
  if (!url) return undefined;
  let host: string;
  try {
    host = new URL(url).hostname.replace(/^www\./, '').toLowerCase();
  } catch {
    return undefined;
  }
  if (host.endsWith('.fda.gov') || host === 'fda.gov' || host === 'dailymed.nlm.nih.gov') {
    return 'regulatory';
  }
  if (host === 'clinicaltrials.gov') return 'clinical_trial';
  if (host === 'usp.org') return 'standards';
  if (host.endsWith('.cdc.gov') || host === 'cdc.gov' || host.endsWith('.who.int') || host === 'who.int') {
    return 'government_health';
  }
  return 'peer_reviewed';
}
